/**
 * ComingUpPanel: the roster's birthdays and work anniversaries for the
 * next six weeks, grouped under This week / Next week / Later this month
 * / Next month (an empty bucket has no header) and rendered in full --
 * this is the "full grouped list" the Coming up band's popover shows
 * (ComingUpBand.tsx), so it never caps or truncates its own output; the
 * band already decided, before opening the popover, that there was more
 * to see.
 *
 * Each row carries the birthday/anniversary mark as real text (🎂/🎉,
 * teamDirectory.ts BIRTHDAY_MARK/ANNIVERSARY_MARK) alongside the words the
 * row already read in ("birthday", "5 years"), plus an aria-label so a
 * screen reader gets the same "Birthday" / "Work anniversary" wording the
 * band's chips use (occasionChipAriaLabel) rather than the mark alone.
 */

import { type ReactNode } from 'react';
import {
  BIRTHDAY_MARK,
  ANNIVERSARY_MARK,
  groupUpcomingOccasions,
  occasionChipAriaLabel,
  occasionDateLabel,
  occasionEventWords,
  upcomingOccasions,
  type TeamDirectoryPerson,
  type UpcomingOccasion,
} from '@/lib/teamDirectory';
import { SECTION_EYEBROW } from '@/components/workspace/drawer/drawerLayout';

function OccasionRow({ occasion }: { occasion: UpcomingOccasion }): ReactNode {
  const mark = occasion.kind === 'birthday' ? BIRTHDAY_MARK : ANNIVERSARY_MARK;
  return (
    <li
      data-testid={`coming-up-item-${occasion.roster_id}-${occasion.kind}`}
      aria-label={occasionChipAriaLabel(occasion)}
      className="flex items-baseline gap-2.5 py-1 text-sm"
    >
      <span className="tabular-nums text-ink-soft shrink-0" data-testid="coming-up-date">
        {occasionDateLabel(occasion.date)}
      </span>
      {/* Name and event share ONE truncating span (not one each): a long
          name plus its event must end in one ellipsis rather than each
          field fighting the other for the remaining width. */}
      <span className="min-w-0 flex-1 truncate" data-testid="coming-up-text">
        <span className="text-ink font-medium">
          {mark} {occasion.name}
        </span>{' '}
        <span className="text-ink-soft">{occasionEventWords(occasion)}</span>
      </span>
    </li>
  );
}

export interface ComingUpPanelProps {
  people: TeamDirectoryPerson[];
  today: string;
}

export function ComingUpPanel({ people, today }: ComingUpPanelProps): ReactNode {
  const occasions = upcomingOccasions(people, today);
  const groups = groupUpcomingOccasions(occasions, today);

  if (groups.length === 0) {
    return (
      <p className="text-sm text-ink-soft" data-testid="coming-up-full-list-empty">
        The next six weeks are clear.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2.5" data-testid="coming-up-full-list">
      {groups.map((g) => (
        <div key={g.bucket} data-testid={`coming-up-group-${g.bucket}`}>
          <h3 className={SECTION_EYEBROW}>{g.label}</h3>
          <ul className="divide-y divide-sage-800/[6%]">
            {g.occasions.map((o) => (
              <OccasionRow key={`${o.roster_id}-${o.kind}`} occasion={o} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
